/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FieldProps } from "formik";
import findKey from "lodash/findKey";
import pickBy from "lodash/pickBy";
import { Link, Paperclip, XIcon } from "lucide-react";
import Upload from "rc-upload";
import UploadIcon from "@/assets/UploadIcon";
import FilesPortal, {
  FilesPortalProps,
} from "@/components/common/FileUpload/FilesPortal";
import {
  getSignedRequest,
  SignedRequestResponse,
} from "@/components/common/FileUpload/helpers";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme, Theme } from "@/contexts/ThemeContext";

interface FileUploadProps {
  projectId: string;
  name: string;
  value: string[];
  buttonClassName?: string;
  listClassName?: string;
  icon?: boolean;
  showLabel?: boolean;
  filesPortal?: FilesPortalProps["portalContainer"];
  showFiles?: boolean;
  onChange: Function;
  disabled?: boolean;
  customText?: string;
  subText?: string;
  customIcon?: string;
  isDragAndDrop?: boolean;
  setIsUploading?: (value: boolean) => void;
  isNotesAttachment?: boolean;
  iconClassName?: string;
}

interface RcUploadCustomRequestParams {
  action: string;
  data: {
    projectId: string;
  };
  file: File;
  filename: string;
  headers: object;
  onError: Function;
  onProgress: Function;
  onSuccess: Function;
  withCredentials: boolean;
}

interface UploadedFile extends File {
  url?: string;
}

interface FileAttachments {
  [id: string]: UploadedFile;
}
interface FileProgress {
  [id: string]: { value: number; error: boolean };
}

const awsSignedRequest = async ({
  data,
  file,
  filename,
  headers,
  onError,
  onProgress,
  onSuccess,
  withCredentials,
  addFileAttachment,
}: RcUploadCustomRequestParams & { addFileAttachment: Function }) => {
  const result: SignedRequestResponse = await getSignedRequest(
    data.projectId,
    file,
  );
  if (!result) {
    onError("Upload failed.");
    return {};
  }

  const fileWithUrl = file as UploadedFile;
  fileWithUrl.url = (result.fileAttachment as { id: string; url?: string }).url;
  addFileAttachment({ [result.fileAttachment.id]: fileWithUrl });

  const formData = new FormData();
  Object.entries(result.fields).forEach(([key, value]) =>
    formData.append(key, value),
  );
  formData.append(filename, file);

  axios
    .post(result.url, formData, {
      withCredentials,
      headers,
      onUploadProgress: ({ total, loaded }) => {
        if (total) {
          onProgress(
            { percent: Math.round((loaded / total) * 100).toFixed(2) },
            file,
          );
        }
      },
    })
    .then(({ data: response }) => {
      onSuccess(response, file);
    })
    .catch((err) => onError(err));

  return {
    abort() {
      alert("Upload aborted.");
    },
  };
};

const NewUpload: React.FC<FileUploadProps> = ({
  projectId,
  name,
  value,
  onChange,
  buttonClassName,
  listClassName,
  filesPortal,
  disabled,
  showFiles = true,
  customText = "Upload additional files",
  subText,
  isDragAndDrop,
  setIsUploading,
  icon,
  isNotesAttachment = false,
  iconClassName,
}) => {
  const { theme } = useTheme();

  const [fileAttachments, setFileAttachments] = useState<FileAttachments>({});
  const [fileProgress, setFileProgress] = useState<FileProgress>({});

  useEffect(() => {
    const newAttachments = pickBy(fileAttachments, (_, id) =>
      value.includes(id),
    );
    setFileAttachments(newAttachments);
  }, [value]);

  const addFileAttachment = useCallback(
    (attachments: FileAttachments) => {
      setFileAttachments((prev) => {
        const updated = { ...prev, ...attachments };
        if (setIsUploading) setIsUploading(true);
        return updated;
      });
    },
    [setIsUploading],
  );

  const progressFile = (file: File, percent: number) => {
    const id = findKey(fileAttachments, file);
    if (!id) return;
    setFileProgress((prev) => ({
      ...prev,
      [id]: { value: percent, error: false },
    }));
  };

  const completeFile = (file: File) => {
    const id = findKey(fileAttachments, file);
    if (!id) return;
    const updatedProgress = { ...fileProgress };
    delete updatedProgress[id];
    if (setIsUploading && Object.keys(updatedProgress).length === 0) {
      setIsUploading(false);
    }
    setFileProgress(updatedProgress);

    onChange({
      target: {
        name,
        value: Object.keys(fileAttachments),
        keys: fileAttachments,
      },
    });
  };

  const removeFileAttachment = (id: string) => {
    setFileAttachments((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setFileProgress((prev) => {
      const { [id]: _, ...rest } = prev;
      if (setIsUploading && Object.keys(rest).length === 0) {
        setIsUploading(false);
      }
      return rest;
    });
    onChange({
      target: {
        name,
        value: Object.keys(fileAttachments).filter((k) => k !== id),
        keys: fileAttachments,
      },
    });
  };

  const onError = (err: Error, file: File) => {
    const id = findKey(fileAttachments, file);
    if (!id) return;
    setFileProgress((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { value: 0 }), error: true },
    }));
    alert(`Error uploading: ${err}`);
  };

  const totalFiles = Object.keys(fileAttachments).length;
  const uploading = Object.keys(fileProgress).length > 0;

  const progress = uploading
    ? Math.round(
        ((totalFiles - Object.keys(fileProgress).length) * 100 +
          Object.values(fileProgress).reduce((sum, p) => sum + +p.value, 0)) /
          totalFiles,
      )
    : 0;

  const textLabel = uploading ? `Uploading...${progress} %` : customText;

  const attachmentsList = Object.keys(fileAttachments).length > 0 && (
    <ul className={cn("space-y-2", listClassName)}>
      {Object.entries(fileAttachments).map(([id, file]) => {
        const fileProg = fileProgress[id];
        return (
          <li
            key={id}
            className="bg-accent-foreground flex min-w-0 justify-between gap-3 rounded-md p-2"
          >
            <span
              className={cn(
                "min-w-0 flex-1 whitespace-pre-wrap wrap-anywhere",
                fileProg?.error && "w-40",
              )}
            >
              {file.name}
            </span>
            <div className="flex shrink-0 items-center space-x-2">
              <Button
                className="h-4 w-4 bg-transparent"
                onClick={() => removeFileAttachment(id)}
                type="button"
              >
                <XIcon className={cn(fileProg?.error && "text-red-500")} />
              </Button>
              {fileProg?.error ? (
                <span className="text-sm text-nowrap text-red-500">
                  Upload failed
                </span>
              ) : (
                fileProg?.value && <div>{Math.round(fileProg.value)}%</div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  const attachments = filesPortal ? (
    <FilesPortal portalContainer={filesPortal}>{attachmentsList}</FilesPortal>
  ) : (
    attachmentsList
  );

  return (
    <>
      {showFiles && attachments}
      <Upload
        data={{ projectId }}
        onSuccess={(_result: object, file: File) => {
          completeFile(file);
        }}
        onError={(err: Error, _: Response, file: File) => onError(err, file)}
        onProgress={({ percent }: { percent: number }, file: File) => {
          progressFile(file, percent);
        }}
        customRequest={(params: RcUploadCustomRequestParams) =>
          awsSignedRequest({
            addFileAttachment,
            ...params,
          })
        }
        multiple
        openFileDialogOnClick={!disabled}
      >
        {icon ? (
          <Button type="button" className={cn("bg-accent", iconClassName)}>
            <Paperclip className="h-4 w-4" />
          </Button>
        ) : isDragAndDrop ? (
          <div
            className={cn(
              "cursor-pointer rounded-md border border-dashed p-4 text-center",
              buttonClassName,
            )}
          >
            <div className="text-sm">
              {customText}
              {subText && <span className=""> {subText}</span>}
            </div>
          </div>
        ) : !isNotesAttachment ? (
          <Button
            className="text-service-paint bg-tertiary h-auto w-full justify-start !px-4 !py-4"
            disabled={disabled}
            type="button"
          >
            <div className="bg-primary rounded-[6px] p-2">
              <UploadIcon
                color={theme === Theme.Light ? "#1d1e1f" : undefined}
              />
            </div>
            {textLabel}
          </Button>
        ) : (
          <Button
            className={cn(
              "text-muted-foreground hover:text-primary-foreground absolute !bg-transparent",
            )}
            style={{
              bottom:
                Object.keys(fileAttachments).length > 0
                  ? `calc(1rem + ${
                      Object.values(fileAttachments).length * 36 +
                      (Object.values(fileAttachments).length - 1) * 8
                    }px)`
                  : "0.5rem",
            }}
            type="button"
          >
            <Link className="!h-4 !w-4" />
          </Button>
        )}
      </Upload>
    </>
  );
};

interface FileUploadFieldProps extends FieldProps {
  projectId: string;
  buttonClassName?: string;
  listClassName?: string;
  disabled?: boolean;
  showFiles?: boolean;
  onChange?: Function;
  showLabel?: boolean;
  customText?: string;
  subText?: string;
  customIcon?: string;
  icon?: boolean;
  isDragAndDrop?: boolean;
  setIsUploading?: (value: boolean) => void;
  isNotesAttachment?: boolean;
  iconClassName?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  projectId,
  field,
  disabled,
  setIsUploading,
  isNotesAttachment = false,
  ...otherProps
}) => (
  <NewUpload
    projectId={projectId}
    name={field.name}
    value={field.value}
    onChange={field.onChange}
    disabled={disabled}
    isNotesAttachment={isNotesAttachment}
    {...otherProps}
    setIsUploading={setIsUploading}
  />
);

export default FileUploadField;
